import React, { useContext, useEffect, useState } from "react";
import {
  Routes,
  Route,
  Navigate,
  useParams,
  useLocation,
} from "react-router-dom";
import { AuthContext } from "../../../../context/AuthContext";
import Navbar from "../../../ui/Navbar";
import ManageTest from "./ManageTest";
import ManageEnrollments from "./ManageEnrollments";
import ManageRubric from "./ManageRubric";
import { fetchWithAuth } from "../../../../utils/api";
import ManageQuestions from "./ManageQuestions";
import EnableFeatureModal from "../../../ui/EnableFeatureModal";
import { HOMEWORK_GRADING_FEATURE } from "../../../../config/featurePromotions";

export default function ManageClassHome() {
  const serverUrl = process.env.REACT_APP_SERVER_URL;
  const { isAuthenticated, user, primaryRole } = useContext(AuthContext);
  const { classId } = useParams();
  const location = useLocation();
  const [classInfo, setClassInfo] = useState({ id: null, name: "Loading..." });
  const [isHomeworkModalOpen, setIsHomeworkModalOpen] = useState(false);

  // Simulate fetching class information based on class ID
  useEffect(() => {
    fetchWithAuth(
      `${serverUrl}/api/breadcrumbs/get?${new URLSearchParams({ classId })}`,
      { credentials: "include" }
    )
      .then((res) => {
        if (!res) return;
        return res.json();
      })
      .then((data) => {
        if (data.class.id) {
          setClassInfo({ name: data.class.name, id: data.class.id });
        } else {
          setClassInfo({ id: null, name: "Unknown Class" });
        }
      })
      .catch((err) => {
        setClassInfo({ id: null, name: "Unknown Class" });
      });
  }, [classId, serverUrl]);

  const [allowHomeworkGrading, setAllowHomeworkGrading] = useState(
    Boolean(
      user?.institute?.allowHomeworkGrading ||
      user?.allowHomeworkGrading
    )
  );

  useEffect(() => {
    const instId =
      user?.instituteId ||
      user?.institute?._id ||
      (typeof user?.institute === "string" ? user?.institute : null);

    if (instId) {
      fetchWithAuth(`${serverUrl}/api/institute/get/${instId}`, {
        credentials: "include",
      })
        .then((res) => {
          if (!res || !res.ok) return null;
          return res.json();
        })
        .then((data) => {
          if (data && data.allowHomeworkGrading !== undefined) {
            setAllowHomeworkGrading(Boolean(data.allowHomeworkGrading));
          }
        })
        .catch(() => {});
    }
  }, [user, serverUrl]);

  if (!isAuthenticated || primaryRole !== "instructor") {
    return <Navigate to="/login" replace />;
  }

  // Determine the current page based on the URL
  const pathParts = location.pathname.split("/").filter(Boolean);
  const lastSegment = pathParts[pathParts.length - 1];
  const currentPath =
    !lastSegment || lastSegment === classId ? "test" : lastSegment.toLowerCase();

  const isLanguageInst =
    user?.category === "LANGUAGE_LEARNING_INSTITUTE" ||
    user?.institute?.category === "LANGUAGE_LEARNING_INSTITUTE";

  const navLinks = [
    {
      text: isLanguageInst ? "Exams & Quizzes" : "Tests",
      to: `/instructor/manage-class/${classId}`,
      id: `test-${classId}`, // Unique ID
      icon: isLanguageInst ? "fa-solid fa-file-signature" : "fa-solid fa-file",
      end: true,
    },
    {
      text: "Homeworks",
      to: `/instructor/manage-class/${classId}/homeworks`,
      id: `homeworks-${classId}`, // Unique ID
      icon: "fa-solid fa-book-open",
      locked: !allowHomeworkGrading,
      onClick: !allowHomeworkGrading
        ? (e) => {
            e?.preventDefault?.();
            setIsHomeworkModalOpen(true);
          }
        : undefined,
    },
    !isLanguageInst && {
      text: "Rubrics",
      to: `/instructor/manage-class/${classId}/rubrics`,
      id: `rubrics-${classId}`, // Unique ID
      icon: "fa-solid fa-toolbox",
    },
    {
      text: isLanguageInst ? "Batch Students" : "Enrollments",
      to: `/instructor/manage-class/${classId}/enrollments`,
      id: `enrollments-${classId}`, // Unique ID
      icon: "fa-solid fa-users",
    },
    {
      text: isLanguageInst ? "Question Bank & Papers" : "Question Bank",
      to: `/instructor/manage-class/${classId}/questions`,
      id: `questions-${classId}`, // Unique ID
      icon: "fa-solid fa-book",
    },
  ].filter(Boolean);

  const breadcrumbs = [
    { name: isLanguageInst ? "Batches" : "Home", path: "/instructor", id: "home" },
    {
      name: `${classInfo.name}`,
      path: `/instructor/manage-class/${classInfo.id}`,
      isLast: false,
      id: `class-${classInfo.id}`,
    },
    {
      name:
        currentPath === "enrollments"
          ? isLanguageInst ? "Batch Students" : "Enrollments"
          : currentPath === "rubrics"
          ? "Rubrics"
          : currentPath === "questions"
          ? isLanguageInst ? "Question Bank & Papers" : "Question Bank"
          : currentPath === "homeworks" || currentPath === "homework"
          ? "Homeworks"
          : isLanguageInst ? "Exams & Quizzes" : "Tests",
      path:
        currentPath === "enrollments"
          ? `/instructor/manage-class/${classId}/enrollments`
          : currentPath === "rubrics"
          ? `/instructor/manage-class/${classId}/rubrics`
          : currentPath === "questions"
          ? `/instructor/manage-class/${classId}/questions`
          : currentPath === "homeworks" || currentPath === "homework"
          ? `/instructor/manage-class/${classId}/homeworks`
          : `/instructor/manage-class/${classId}`,
      isLast: true,
      id:
        currentPath === "enrollments"
          ? "enrollments-crumb"
          : currentPath === "rubrics"
          ? "rubrics-crumb"
          : currentPath === "questions"
          ? "questions-crumb"
          : currentPath === "homeworks" || currentPath === "homework"
          ? "homeworks-crumb"
          : "tests-crumb",
    },
  ];

  return (
    <>
      <Navbar links={navLinks} breadcrumbs={breadcrumbs} />
      <div className="manage-class-content">
        <Routes>
          <Route index element={<ManageTest key="tests" classId={classId} defaultTab="tests" />} />
          <Route path="tests" element={<Navigate to={`/instructor/manage-class/${classId}`} replace />} />
          <Route
            path="homeworks"
            element={
              allowHomeworkGrading ? (
                <ManageTest key="homeworks" classId={classId} defaultTab="homeworks" />
              ) : (
                <Navigate to={`/instructor/manage-class/${classId}`} replace />
              )
            }
          />
          <Route path="homework" element={<Navigate to={`/instructor/manage-class/${classId}/homeworks`} replace />} />
          <Route path="questions" element={<ManageQuestions classId={classId} />} />
          <Route path="rubrics" element={<ManageRubric classId={classId} />} />
          <Route
            path="enrollments"
            element={<ManageEnrollments classId={classId} />}
          />
          <Route path="*" element={<Navigate to="." replace />} />
        </Routes>
      </div>

      {/* Enable Feature Modal for Homework Grading */}
      <EnableFeatureModal
        isOpen={isHomeworkModalOpen}
        onClose={() => setIsHomeworkModalOpen(false)}
        {...HOMEWORK_GRADING_FEATURE}
        instituteName={
          user?.institute?.name ||
          user?.instituteName ||
          "Our Institute"
        }
        instituteCode={
          user?.institute?.code ||
          user?.instituteCode ||
          ""
        }
        userName={
          user?.name ||
          user?.username ||
          `${user?.firstName || ""} ${user?.lastName || ""}`.trim() ||
          "Instructor"
        }
        userEmail={user?.email || ""}
        userRole={user?.role || primaryRole || "instructor"}
        whatsappNumber="919381369640"
        supportEmail="support@grademe-ai.com"
      />
    </>
  );
}
